import React, { useEffect, useState } from 'react';
import { ConfigProvider, App as AntdApp, Avatar, Button, Dropdown, Layout, Menu, Result, Tooltip } from 'antd';
import { MenuOutlined, LogoutOutlined, UserOutlined } from '@ant-design/icons';

import AuthGate from './auth/AuthGate.jsx';
import BusinessDayChip from './businessDay/BusinessDayChip.jsx';
import BusinessDayProvider from './businessDay/BusinessDayProvider.jsx';
import dropdownUrl from './assets/dropdown.svg';
import logoUrl from './assets/lynear-wealth-logo.svg';
import notificationUrl from './assets/notification.svg';
import Loader from './components/Loader.jsx';
import NavIcon from './components/NavIcon.jsx';
import { useAccess } from './auth/AccessContext.jsx';
import { ANTD_PREFIX, antdTheme } from './config/theme';
import UploadsTab from './containers/uploads/UploadsTab';
import TransactionsTab from './containers/transactions/TransactionsTab';
import UserManagement from './containers/userManagement/UserManagement.jsx';
import AnalyticsFeedsTab from './containers/analyticsFeeds/AnalyticsFeedsTab.jsx';
import ProfilePage from './containers/profile/ProfilePage.jsx';
import SettingsTab from './containers/settings/SettingsTab.jsx';
import NotificationBell from './notifications/NotificationBell.jsx';
import NotificationProvider from './notifications/NotificationProvider.jsx';
import LiveProvider from './live/LiveProvider.jsx';
import { hashFromOpenQuery } from './notifications/notifications.js';

const { Header, Sider, Content, Footer } = Layout;

/*
 * File Uploads, the Unit Registry, Analytics Feeds and Settings are the Core's modules; User Management
 * is the Console's own (IP-78): invitations, roles and claims, authenticators. Each shows
 * when the person's claims open at least one of its screens (@lfoc/domain/permissions.js).
 * Profile is everyone's, and opens from the name at the foot of the navigation.
 */
const MODULES = [
    {
        key: 'uploads',
        icon: 'upload',
        label: 'File Uploads',
        screens: ['uploads'],
        render: () => <UploadsTab />
    },
    {
        // Key stays `transactions`: the registry's deep links are `#transactions/registry/<fundId>/…`.
        key: 'transactions',
        icon: 'grid',
        label: 'Unit Registry',
        screens: ['unitPrices', 'approval', 'registry', 'reports'],
        render: () => <TransactionsTab />
    },
    {
        // The day-end analytics feeds (IP-142 to IP-156): a module of its own since 9 Oct 2026.
        key: 'analyticsFeeds',
        icon: 'feeds',
        label: 'Analytics Feeds',
        screens: ['analyticsFeeds'],
        render: () => <AnalyticsFeedsTab />
    },
    {
        key: 'userManagement',
        icon: 'users',
        label: 'User Permission',
        screens: ['userManagement'],
        render: () => <UserManagement />
    },
    {
        key: 'settings',
        icon: 'settings',
        label: 'Settings',
        screens: ['fundConfig', 'calendar'],
        render: () => <SettingsTab />
    }
];

/**
 * Active module is mirrored into the URL hash so a view can be linked to and reloaded into. The
 * hash is `#<module>` or `#<module>/<section>`; the section segment belongs to the module's own
 * section tabs (see TransactionsTab), so only the first segment is read here.
 */
const moduleFromHash = (keys) => {
    const key = window.location.hash.replace('#', '').split('/')[0];
    return keys.includes(key) ? key : keys[0];
};

/**
 * The signed-in workspace: module navigation in a collapsible left sider, the module's section tabs
 * across the top of the content. The Sider / toggle / inline Menu arrangement is the LIV web app's
 * own (its information directory), so the Console reads as part of the product it is mounted in.
 */
const Workspace = () => {
    const { principal, canView, signOut } = useAccess();
    // A module shows when the Core granted at least one of its screens.
    const modules = MODULES.filter((m) => m.screens.some(canView));
    const moduleKeys = [...modules.map((m) => m.key), 'profile'];
    const [activeKey, setActiveKey] = useState(() => {
        // An email's link (docs/notifications/implementation-guide.md 5.3) is ?open=<screen>&id=<entity>:
        // turned into the hash route once, and the query dropped so a reload does not open it again.
        const opened = hashFromOpenQuery(window.location.search);
        if (opened) {
            window.history.replaceState(null, '', window.location.pathname + '#' + opened);
        }
        return moduleFromHash(moduleKeys);
    });
    const [collapsed, setCollapsed] = useState(false);
    // A notification opens its screen by setting the hash (notifications/NotificationBell.jsx). Follow it,
    // and remount the module (navNonce) so its section tabs read the new hash.
    const [navNonce, setNavNonce] = useState(0);
    useEffect(() => {
        const onHash = () => {
            setActiveKey(moduleFromHash(moduleKeys));
            setNavNonce((n) => n + 1);
        };
        window.addEventListener('hashchange', onHash);
        return () => window.removeEventListener('hashchange', onHash);
        // moduleKeys is derived from the person's claims, which do not change while signed in.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onNavigate = ({ key }) => {
        setActiveKey(key);
        window.location.hash = key;
    };

    // Only the active module is mounted, so each one's form state is fresh when you come back to it.
    const active = activeKey === 'profile'
        ? { key: 'profile', render: () => <ProfilePage /> }
        : modules.find((m) => m.key === activeKey);
    const toggleLabel = collapsed ? 'Expand navigation' : 'Collapse navigation';
    const avatarUrl = principal.avatarUrl ?? principal.picture ?? null;
    const initials = [principal.firstName, principal.lastName]
        .filter(Boolean)
        .map((name) => name.charAt(0).toUpperCase())
        .join('')
        .slice(0, 2);
    const profileRole = (principal.roles[0] || 'NO_ROLE')
        .toLowerCase()
        .split(/[_\s-]+/)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
    const profileMenu = {
        items: [
            { key: 'profile', icon: <UserOutlined />, label: 'View user profile' },
            { type: 'divider' },
            { key: 'signout', icon: <LogoutOutlined />, label: 'Sign out' }
        ],
        onClick: ({ key }) => {
            if (key === 'profile') onNavigate({ key: 'profile' });
            if (key === 'signout') signOut();
        }
    };

    return (
        <Layout className="console-shell">
            <Header className="console-topbar">
                <div className="console-topbar-brand" aria-label="Lynear Wealth Management">
                    <img src={logoUrl} alt="" />
                </div>
                <div className="console-topbar-actions">
                    <BusinessDayChip />
                    {/* The bell: the unread count pushed over the socket, and the drawer (notifications/NotificationBell.jsx). */}
                    <NotificationBell icon={<img src={notificationUrl} alt="" className="console-notification-icon" />} />
                    <Dropdown trigger={['click']} placement="bottomRight" menu={profileMenu}>
                        <button type="button" className={`console-profile-trigger ${activeKey === 'profile' ? 'is-active' : ''}`}
                            aria-label="Open profile menu">
                            <Avatar size={26} src={avatarUrl}>{avatarUrl ? null : (initials || <UserOutlined />)}</Avatar>
                            <span className="console-profile-role">{profileRole}</span>
                            <img src={dropdownUrl} alt="" className="console-profile-chevron" />
                        </button>
                    </Dropdown>
                </div>
            </Header>
            <Layout className="console-main" hasSider>
                <Sider
                    className="console-sider"
                    theme="light"
                    width={240}
                    collapsible
                    collapsed={collapsed}
                    trigger={null}
                    breakpoint="lg"
                    onBreakpoint={setCollapsed}
                >
                    <div className={`console-sider-header ${collapsed ? 'is-collapsed' : ''}`}>
                        <Tooltip placement="right" title={toggleLabel}>
                            <button
                                type="button"
                                className="console-sider-toggle"
                                onClick={() => setCollapsed((c) => !c)}
                                aria-label={toggleLabel}
                                aria-expanded={!collapsed}
                            >
                                <MenuOutlined />
                            </button>
                        </Tooltip>
                        {!collapsed && <span className="console-sider-title">Fund Operations</span>}
                    </div>
                    <hr className="console-sider-separator" />
                    <Menu
                        mode="inline"
                        inlineCollapsed={collapsed}
                        selectedKeys={[activeKey]}
                        onClick={onNavigate}
                        items={modules.map(({ key, icon, label }) => ({
                            key,
                            icon: <NavIcon name={icon} active={activeKey === key} />,
                            label
                        }))}
                    />
                </Sider>
                <Content className="console-body">
                    {active ? (
                        <React.Fragment key={activeKey + ':' + navNonce}>{active.render()}</React.Fragment>
                    ) : (
                        // Signed in, but no role opens a screen yet: the profile is still theirs.
                        <Result
                            status="403"
                            title="Nothing to show yet"
                            subTitle="Your roles open no screen in Fund Operations. A Fund Operations administrator assigns roles."
                            extra={<Button onClick={() => onNavigate({ key: 'profile' })}>Your profile</Button>}
                        />
                    )}
                </Content>
            </Layout>
            <Footer className="console-footer">
                © {new Date().getFullYear()} LYNEAR Wealth Management (Pvt) Limited. All Rights Reserved.
            </Footer>
        </Layout>
    );
};

/**
 * Providers, then the gate: nothing below AuthGate renders until the operator
 * has signed in - password and authenticator code - and the Core has said
 * what they may see.
 *
 * @param {{ config: { apiBase: string } }} props
 */
const Console = ({ config }) => (
    // Every antd Spin (table loading and the like) shows LIV's loader rather than antd's dots.
    <ConfigProvider prefixCls={ANTD_PREFIX} theme={antdTheme} spin={{ indicator: <Loader size="large" /> }}>
        <AntdApp className="lfoc-styles-wrapper">
            <AuthGate config={config}>
                {/* Live screens (live/LiveProvider.jsx): the hub every screen's useLiveTopic listens to - around everything that listens. */}
                <LiveProvider>
                    {/* The business day's one read (GET /books/days), inside the access context, around the Workspace. */}
                    <BusinessDayProvider>
                        {/* The bell's state and the tab's one socket (notifications/NotificationProvider.jsx), which feeds the live hub too. */}
                        <NotificationProvider>
                            <Workspace />
                        </NotificationProvider>
                    </BusinessDayProvider>
                </LiveProvider>
            </AuthGate>
        </AntdApp>
    </ConfigProvider>
);

export default Console;
